/** @jsxImportSource @emotion/react */
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {IAppState} from '../../../../core/redux/IAppState';
import {
  getDescription,
  getGuestHotlists,
  getSeason,
  isActive,
  isDeferred, nameWithSeason,
  sortByExpirationDateAsc,
  typeFor,
} from '../../module';
import * as R from 'ramda';
import {getError, getLoading} from '../../../../core/redux/api/module';
import {ACTION_TYPE} from '../../../../core/redux/actionType';
import {dtwIsHotlisted} from './module';
import {isWaived} from '../../waivers/module';
import {useEffect, useMemo, useRef, useState} from 'react';
import {Redemption} from '../redemption';
import {redeemEntitlementAction} from '../redeemEntitlementAction';
import {GuestDetails} from '../../../guestDetails';
import {submitRedemptionLocalRefAction} from '../submitRedemptionLocalRefAction';
import {getDtwEntitlementsAction} from './getDtwEntitlementsAction';
import {clearRedeemedEntitlementAction} from '../clearRedeemedEntitlementAction';
import {Entitlement} from '../../models';
import {assoc} from 'ramda';
import {apostrophize} from '../../../../language/strings';
import {fullName} from '../../../module';
import {css} from '@emotion/react';
import {css as othercss} from '@emotion/css';
import {StyledBoxedMessage} from '../../../../forms/elements/boxedMessage/StyledBoxedMessage';
import {BoxedErrorInverse} from '../../../../forms/elements/boxedMessage/boxedMessageStyles';
import {LargeText, MediumText} from '../../../../styles/styles';
import ImmutableMap from '../../../../language/ImmutableMap';
import {Row} from '../../../../layout/flexbox';
import {COLUMN_SPACING, ROW_SPACING} from '../../../../forms/styles';
import {BigNeutral, BigSecondary} from '../../../../forms/elements/buttons';
import ModalView, {ModalContent} from '../../../../layout/modal/ModalView';
import {MODAL_SIZE} from '../../../../layout/modal/modalSize';
import ModalTitleHeader from '../../../../layout/modal/ModalTitleHeader';
import dtw from '../../../../assets/icons/daypass.svg';
import LabeledData from '../../../../forms/elements/LabeledData';
import LabelGroup from '../../../../forms/elements/LabelGroup';
import {Dropdown} from '../../../../forms/elements/select/Dropdown';
import {Loader} from '../../../../forms/elements/loader/Loader';
import ErrorView from '../../../../forms/ErrorView';
import {isNilOrEmpty} from '../../../../language/module';
import {RedemptionDetails} from '../RedemptionDetails';
import {getAllBlackoutDates, getFriendlyDays, isBlackoutDate} from '../module';
import ClamShell from '../../../../layout/ClamShell';
import DaysToRedeemSelect from '../DaysToRedeemSelect';
import checkmark from '../../../../assets/icons/checkCircleFilled.svg';
import {RedemptionIdForm} from '../RedemptionIdForm';
import { getResource } from '../../../../language/resources/getResource';
import DateInput from '../../../../forms/elements/DateInput';
import { DatePickerDateWithMessage } from '../../../../forms/elements/models';
import { getLiftBlackoutDateMetadataAction } from './getLiftBlackoutDateMetadataAction';
import { normalizeToLocalISO } from '../../../../language/time';

const BLACKOUT_INVALID_DESCRIPTION = 'black-out';

const Embolden = css`
  ${MediumText};
  font-weight: bold;
`;

const RedemptionComplete = css`
  display: flex !important;
  align-items: center;
  justify-content: center;
  ${LargeText};
  font-weight: bold;
  margin-bottom: 25px;
`;

const DTWEntitlementModal = css`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  ${COLUMN_SPACING};
`;

const DTWEntitlementDaysSelect = css`
  width: 100%; 
  align-self: 
  flex-start; 
  box-sizing: border-box;
`;

const DTWEntitlementRedeemDate = css `
  margin-bottom: 15px;
  width: 150px; 
`

const DTWEntitlementLoader = css`
  align-self: center;
  height: 120px;
  width: 120px;
  margin: 9px 0 !important; /* Fill space of form we're loading data for */
`;

const DTWRedemptionLoader = css`
  align-self: center; 
  height: 120px; 
  width: 120px;
`

const BlackoutCalendarDay = othercss({
  backgroundColor: '#AD4A4A',
  borderRadius: '5px',
  '&:hover': {
    backgroundColor: '#AD4A4A',
  },
})

type Props = {
  guestId: string;
  onDismiss: () => void;
}
export const DtwEntitlementModal = (props : Props) => {

  const guest = useSelector((state: IAppState) => state.guests.get(props.guestId) as GuestDetails);
  const selectedResortAbbreviation = useSelector((state: IAppState) => state.selectedResort.resortAbbreviation);
  const isLoadingEntitlements = useSelector((state: IAppState) => !!getLoading(state, props.guestId, ACTION_TYPE.GET_DTW_ENTITLEMENTS_REQUEST));
  const isLoadingBlackoutDateMetadata = useSelector((state: IAppState) => 
    !!getLoading(state, state.selectedResort.resortAbbreviation, ACTION_TYPE.GET_LIFT_BLACKOUTDATE_METADATA_REQUEST)
  );  
  const entitlementLoadError = useSelector((state: IAppState) => getError(state, props.guestId, ACTION_TYPE.GET_DTW_ENTITLEMENTS_REQUEST)?.errorMsg);
  const allEntitlementTypes = useSelector((state: IAppState) => state.entitlementTypesDetails?.allEntitlementTypes);
  const entitlements = useSelector((state: IAppState): Entitlement[] => {
    const guest = state.guests.get(props.guestId) as GuestDetails;
    const hotlists = getGuestHotlists(state);
    return sortByExpirationDateAsc(
      (guest.directToWindow || []).filter(
        ent => isActive(ent)
          && !isDeferred(ent)
          && !dtwIsHotlisted(ent, guest, hotlists)
          && isWaived(typeFor(ent, state.entitlementTypesDetails?.allEntitlementTypes), guest.waivers),
      ),
    ) as Entitlement[];
  }, shallowEqual);
  const isRedeeming = useSelector((state: IAppState) => !!getLoading(state, props.guestId, ACTION_TYPE.REDEEM_ENTITLEMENT_REQUEST));
  const redemptionError = useSelector((state: IAppState) => getError(state, props.guestId, ACTION_TYPE.REDEEM_ENTITLEMENT_REQUEST)?.errorMsg);
  const blackoutDateMetadata = useSelector((state: IAppState) => state.liftBlackoutDateMetadata);
  const resources = useSelector((state: IAppState) => state.locale);
  const dateFormat = useSelector((state: IAppState) => state.defaultDateFormat);
  const userRole = useSelector((state: IAppState) => state.userState.role);
  const redeemedEntitlement = useSelector((state: IAppState) => state.redeemedEntitlement);

  const warnBeforeRedemption = true

  const dispatch = useDispatch();
  const redeemEntitlement = (quantity: number, entitlementId: number, date: Date) =>  {
    const redemption: Redemption = {
      guestId:            guest.masterGuestId,
      entitlementId,
      redemptionDate:     date,
      redemptionQuantity: quantity,
    };

    dispatch(redeemEntitlementAction(redemption))
  }
  const submitLocalRef = (guest: GuestDetails, redemption: Redemption) => dispatch(
    submitRedemptionLocalRefAction(redemption, guest),
  )

  const [quantity, setQuantity] = useState(0);
  const [selectedPassId, setSelectedPassId] = useState<number | undefined>(undefined);
  const [showConfirmationMessage, setShowConfirmationMessage] = useState(false);
  const [redeemDate, setRedeemDate] = useState<Date | undefined>(undefined);
  const [blackoutDates, setBlackoutDates] = useState<Date[]>([]);
  const [datePickerBlackoutDates, setDatePickerBlackoutDates] = useState<DatePickerDateWithMessage[]>([]);

  const blackoutMessage = getResource(resources, 'BLACKED_OUT');
  const selectedPass = useMemo(
    () => entitlements.find(e => e.entitlementId === selectedPassId) ?? entitlements[0],
    [entitlements, selectedPassId]
  );

  const onChangeRedeemDate = (date: Date | null) => {
    if (date === null) {  
      setRedeemDate(undefined);
    } else {
    setRedeemDate(date);}
  };

  useEffect(() => {
    dispatch(clearRedeemedEntitlementAction());
    dispatch(getDtwEntitlementsAction(props.guestId));
  }, [dispatch, props.guestId]);

  // separate effect for setting initial selected pass
  useEffect(() => {
    if (entitlements?.length && !selectedPassId) {
      setSelectedPassId(entitlements[0].entitlementId);
    }
  }, [entitlements, selectedPassId]);


  useEffect(() => {
    const theseEntitlements = entitlements ?? [];
    // If no entitlements, clear selection once
    if (theseEntitlements.length === 0) {
      if (selectedPass) {
        setSelectedPassId(undefined);
        setRedeemDate(undefined);
      }
      return;
    }
    
    const stillExists = entitlements.some(e => e.entitlementId === selectedPassId);
    if (!stillExists) {
      setSelectedPassId(entitlements[0].entitlementId);
    }

    const isBlackoutDate = blackoutDates.some(
      (blackoutDate) =>
        redeemDate &&
        blackoutDate.toDateString() === redeemDate.toDateString()
    );

    if (selectedPass?.invalidDescription === BLACKOUT_INVALID_DESCRIPTION && isBlackoutDate) {
      setRedeemDate(undefined);
    }

  }, [entitlements, selectedPass, redeemDate, selectedPassId, blackoutDates]);


  useEffect(() => {
    if (selectedResortAbbreviation) {
      dispatch(getLiftBlackoutDateMetadataAction(selectedResortAbbreviation));
    }
  }, [selectedResortAbbreviation, dispatch]);

  const redeemDateRef = useRef(redeemDate);
  useEffect(() => {
    redeemDateRef.current = redeemDate;
  });

  useEffect(() => {
    if (selectedPass === undefined) {
      setBlackoutDates([]);
      setDatePickerBlackoutDates([]);
      setRedeemDate(undefined);
      return;
    }

    if (selectedPass.isValid) {
      setRedeemDate(prev => prev ?? new Date()); // only set if not already set
    }

    if (!isLoadingBlackoutDateMetadata && blackoutDateMetadata && blackoutDateMetadata.length > 0) {
      const metadata = blackoutDateMetadata.filter((m) => m.entitlementTypeId === selectedPass.entitlementTypeId);
      if (metadata && metadata.length > 0) {
        const newBlackoutDates = getAllBlackoutDates(metadata);
        setBlackoutDates(newBlackoutDates);
        setDatePickerBlackoutDates(newBlackoutDates.map((date) => ({
          date,
          message: blackoutMessage,
        } as DatePickerDateWithMessage)));

        const checkDate = normalizeToLocalISO(redeemDateRef.current ?? new Date());
        const isBlackedOut = newBlackoutDates.some(d => normalizeToLocalISO(d) === checkDate);
        if (isBlackedOut) {
          setRedeemDate(undefined);
        } else {
          setRedeemDate(prev => prev ?? new Date()); // only set if not already set
        }
      }
    }


  }, [isLoadingBlackoutDateMetadata, selectedPass, blackoutDateMetadata, blackoutMessage]);

  const onRedemptionConfirmed = (quantity: number, date: Date) => {
    const entitlement: Entitlement | undefined = selectedPass;
    // You can't redeem an invalid entitlement (no corrective redemptions in DTW)
    // Allow redemptions on blackout dates for non-blacked out dates
    if ((!entitlement?.isValid && entitlement?.invalidDescription !== BLACKOUT_INVALID_DESCRIPTION) || date === undefined) {
      return;
    }

    setQuantity(0);
    setShowConfirmationMessage(false);

    return redeemEntitlement(quantity, entitlement.entitlementId, date!);
  };

  const getEntitlementId   = (item): string => R.prop('entitlementId', item) || '';
  const getEntitlementName = (allEntitlementTypes) => (item): string =>
    `${getSeason(item, allEntitlementTypes)} ${getDescription(item, allEntitlementTypes)}`;
  const makeRedemptionHandler = (quantity) => () => {
    if (redeemDate === undefined) {
      return;
    }
    onRedemptionConfirmed(quantity, redeemDate);
  }
  const onPassChange = (entitlement: Entitlement) => setSelectedPassId(entitlement.entitlementId);

  const onSubmit = (quantity: number) => {
    if (redeemDate === undefined) {
      return;
    }
    if (!warnBeforeRedemption) {
      return onRedemptionConfirmed(quantity, redeemDate);
    }
    setQuantity(quantity);
    setShowConfirmationMessage(true);
  }

  const saveLocalRef = (localRef: string) => {
    props.onDismiss();

    if (!selectedPass) {
      return;
    }
    submitLocalRef(guest, assoc('localRef', localRef, redeemedEntitlement!));
  }

  const renderConfirmationMessage = () => {
    const possessiveGuestName = apostrophize(fullName(guest));
    const pluralizedDay       = getResource(resources, 'DTW_ENTITLEMENT_MODAL_PLURALIZED_DAY', quantity)

    return (
      <div css={css`padding: 1px 0; /* Collapse cuts off top and bottom a bit... */`}>
        <StyledBoxedMessage
          boxStyle={BoxedErrorInverse}
          message={getResource(resources, 'DTW_ENTITLEMENT_MODAL_MESSAGE')}
        />

        <span css={css`display: block; ${MediumText}; padding-top: 25px;`}>
          {getResource(resources, 'DTW_ENTITLEMENT_MODAL_CONFIRMATION')}<span css={Embolden}>{quantity}</span> {pluralizedDay}&nbsp;
          {getResource(resources, 'DTW_ENTITLEMENT_MODAL_CONFIRMATION_NAME', possessiveGuestName)}
          <span css={Embolden}>
            {
              nameWithSeason(
                selectedPass,
                allEntitlementTypes || ImmutableMap.empty()
              )
            }
          </span>?
        </span>

        <Row justifyContent='flex-end' rootCss={css`padding-top: 25px; padding-bottom: 1px; ${ROW_SPACING};`}>
          <BigSecondary onClick={()=>setShowConfirmationMessage(false)}>{getResource(resources, 'DTW_ENTITLEMENT_MODAL_CANCEL')}</BigSecondary>
          <BigNeutral onClick={makeRedemptionHandler(quantity)}>{getResource(resources, 'DTW_ENTITLEMENT_MODAL_CONFIRM')}</BigNeutral>
        </Row>
      </div>
    );
  }

  const pass          = selectedPass!;
  const cantRedeemMsg = pass?.invalidDescription;
  return (
    <ModalView
      onDismiss={props.onDismiss}
      size={MODAL_SIZE.SMALL}
      contentCss={DTWEntitlementModal}
    >
      <ModalTitleHeader resources={resources} heading={getResource(resources, 'DTW_ENTITLEMENT_MODAL_HEADING')} icon={dtw} onDismiss={props.onDismiss} />
      <ModalContent>
        {/* Guest Name */}
        <LabeledData label={getResource(resources, 'DTW_ENTITLEMENT_MODAL_GUEST_LABEL')} data={fullName(guest)} />

        <LabelGroup label={getResource(resources, 'DTW_ENTITLEMENT_MODAL_PASS_LABEL')} rootCss={css`ROW_GROUP; width:300px;`}>
          <Dropdown
            resources={resources}
            items={entitlements || []}
            getKey={getEntitlementId}
            getValue={getEntitlementId}
            getLabel={getEntitlementName(allEntitlementTypes)}
            noItemsMessage={getResource(resources, 'DTW_ENTITLEMENT_MODAL_NO_ITEMS')}
            onChange={onPassChange}
            selectedItem={pass}
            disabled={
              !!entitlementLoadError
              || !!redeemedEntitlement?.redemptionId
              || showConfirmationMessage
            }
            rootCss={css`width: 350px;  max-width: 100%;`}
          />
        </LabelGroup>

        { // Loading entitlements
          isLoadingEntitlements
          && <Loader
            rootCss={DTWEntitlementLoader}
          />
        }

        <ErrorView
          error={
            !isLoadingEntitlements && !isNilOrEmpty(entitlementLoadError)
              ? getResource(resources, 'DTW_ENTITLEMENT_MODAL_FAILED_FETCH', apostrophize(fullName(guest)))
              : undefined
          }
        />

        <ErrorView
          error={
            !isNilOrEmpty(redemptionError)
              ? getResource(resources, 'DTW_ENTITLEMENT_MODAL_FAILED_REDEMPTION', apostrophize(fullName(guest)))
              : undefined
          }
        />

        { // Redemption Summary
          !isLoadingEntitlements
          && !entitlementLoadError
          && <div><RedemptionDetails
            totalDays={getFriendlyDays(resources, pass?.maxAllowed)}
            daysUsed={getFriendlyDays(resources, pass?.used)}
            remainingDaysAvailable={getFriendlyDays(resources, pass?.available)}
            redemptionHistory={pass?.redemptionHistory || []}
          />
          
          </div>
        }

        {/* No Days Available Message */}
        <ErrorView
          error={
            !isLoadingEntitlements
            && !!pass
            && isNilOrEmpty(cantRedeemMsg)
            && !pass.available
            && !pass.entitlementId
              ? getResource(resources, 'DTW_ENTITLEMENT_MODAL_NO_DAYS')
              : undefined
          }
        />

        {/* Validation Message */}
        <ErrorView
          error={
            !isLoadingEntitlements
            && !pass?.isValid
            && !isNilOrEmpty(cantRedeemMsg)
              ? cantRedeemMsg
              : undefined
          }
        />

        <ClamShell
          rootCss={COLUMN_SPACING}
          hasNestedCollapse={true}
          isOpen={!isLoadingEntitlements && !isRedeeming}
        >
          <div css={COLUMN_SPACING}>
            { // Redeem a Direct-to-Window Entitlement CTA
              !isLoadingEntitlements && !isRedeeming
              && isNilOrEmpty(redeemedEntitlement?.redemptionId)
              && <div>
                {['sysadmin', 'admin', 'manager'].includes(userRole as string) &&
                <LabelGroup
                  label={getResource(resources, 'DTW_ENTITLEMENT_MODAL_REDEEM_LABEL')}
                  rootCss={css`width: auto; width: 9.4rem; margin: 10px 0px;`}
                  subLabel={`(${dateFormat?.toUpperCase()})`}>
                  <DateInput 
                    date={redeemDate}
                    onChange={onChangeRedeemDate}
                    placeholderText={getResource(resources, 'SELECT_DATE_PLACEHOLDER')}
                    tooltipText={getResource(resources, 'REDEEM_DATE_TOOLTIP')}
                    dateFormat={dateFormat}
                    minDate={pass?.effectiveDate as Date}
                    maxDate={pass?.expirationDate as Date}
                    rootCss={DTWEntitlementRedeemDate}
                    dayCss={(date) => isBlackoutDate(date, blackoutDates) ? BlackoutCalendarDay : ''}
                    excludeDates={datePickerBlackoutDates}
                  />
                </LabelGroup>}
                
                <DaysToRedeemSelect
                  onSubmit={onSubmit}
                  remainingDays={pass?.available ?? 0}
                  totalDays={pass?.maxAllowed ?? 0}
                  allowNegativeRedemption={false}
                  disable={
                    !pass
                    || redeemDate === undefined
                    || isLoadingBlackoutDateMetadata
                    || !!entitlementLoadError
                    || (!pass.isValid && cantRedeemMsg !== 'all-used' && pass.invalidDescription !== BLACKOUT_INVALID_DESCRIPTION)
                    || !!redeemedEntitlement?.redemptionId
                    || showConfirmationMessage
                  }
                  rootCss={DTWEntitlementDaysSelect}
                />
              </div>
            }

            { // Confirm Redemption
              showConfirmationMessage && renderConfirmationMessage()
            }

            {/* Redemption Error */}
            <ErrorView error={redemptionError} />

            {!isNilOrEmpty(redeemedEntitlement?.redemptionId)
              && <div css={COLUMN_SPACING}>
                <span css={RedemptionComplete}>
                  <img src={checkmark} alt='✓' css={css`margin-right: 15px;`} />{getResource(resources, 'DTW_ENTITLEMENT_MODAL_REDEMPTION_COMPLETE')}
                </span>
                <RedemptionIdForm
                  resources={resources}
                  redemptionId={redeemedEntitlement?.redemptionId}
                  onLocalRefSave={saveLocalRef}
                />
              </div>
            }
          </div>
        </ClamShell>

        { // Submitting Redemption...
          isRedeeming && <div css={css`padding: 30px 0; /* Fill space of loading content */`}>
            <Loader rootCss={DTWRedemptionLoader} />
          </div>
        }
      </ModalContent>
    </ModalView>
  );
};
